import type {Review, ReviewCycle} from 'client';

const SPRITES_CYCLE_2025_NAME = '2025 SPRiTEs Feedback';

// Self-review statuses that allow viewing answers and generating PDFs
export const VIEWABLE_REVIEW_STATUSES = ['Submitted', 'Sent Back', 'Completed'];

/** Review cycle statuses that enable ReviewGPT and default cycle selection. */
export const REVIEW_GPT_CYCLE_STATUSES = ['OPEN', 'CREATED'] as const;

/** Returns the active review cycle for ReviewGPT (prefers OPEN over CREATED). */
export const findActiveReviewCycle = (
  cycles: ReviewCycle[]
): ReviewCycle | undefined => {
  for (const status of REVIEW_GPT_CYCLE_STATUSES) {
    const match = cycles.find((c) => c.status === status);
    if (match) return match;
  }
  return undefined;
};
const SPRITES_CYCLE_2024_NAME = '2024 SPRiTEs Feedback';
const SPRITES_CYCLE_2023_NAME = '2023 SPRiTEs Feedback';
const SPRITES_CYCLE_2022_NAME = '2022 SPRiTEs Feedback';
const SPRITES_CYCLE_2021_NAME = '2021 SPRiTEs Feedback';
const SPRITES_CYCLE_2020_NAME = '2020 SPRiTEs Feedback';

// MAKE SURE YOU MATCH THE DATA FOUND IN /sprites-gpt-server/api/endpoints/open_ai.py
const spriteDateRanges = [
  {
    start: new Date('2025-02-15'),
    end: new Date('2026-02-14'),
    spriteCycle: SPRITES_CYCLE_2025_NAME,
  },
  {
    start: new Date('2024-02-24'),
    end: new Date('2025-02-14'),
    spriteCycle: SPRITES_CYCLE_2024_NAME,
  },
  {
    start: new Date('2023-01-24'),
    end: new Date('2024-02-23'),
    spriteCycle: SPRITES_CYCLE_2023_NAME,
  },
  {
    start: new Date('2022-01-06'),
    end: new Date('2023-01-23'),
    spriteCycle: SPRITES_CYCLE_2022_NAME,
  },
  {
    start: new Date('2021-01-09'),
    end: new Date('2022-01-05'),
    spriteCycle: SPRITES_CYCLE_2021_NAME,
  },
  {
    start: new Date('2020-01-11'),
    end: new Date('2021-01-08'),
    spriteCycle: SPRITES_CYCLE_2020_NAME,
  },
];

const feedbackTypeToSpritesCycle = (feedbackType: string) => {
  if (feedbackType.includes('2025')) {
    return SPRITES_CYCLE_2025_NAME;
  } else if (feedbackType.includes('2024')) {
    return SPRITES_CYCLE_2024_NAME;
  } else if (feedbackType.includes('2023')) {
    return SPRITES_CYCLE_2023_NAME;
  } else if (feedbackType.includes('2022')) {
    return SPRITES_CYCLE_2022_NAME;
  } else if (feedbackType.includes('2021')) {
    return SPRITES_CYCLE_2021_NAME;
  } else if (feedbackType.includes('2020')) {
    return SPRITES_CYCLE_2020_NAME;
  }

  return null;
};

const dateToSpriteCycle = (dateStr: string) => {
  const date = new Date(dateStr);

  for (const spriteDateRange of spriteDateRanges) {
    if (date >= spriteDateRange.start && date <= spriteDateRange.end) {
      return spriteDateRange.spriteCycle;
    }
  }

  return null;
};

export const getSpriteCycleOfFeedback = (
  feedbackType: string,
  feedbackDate: string
) => {
  return (
    feedbackTypeToSpritesCycle(feedbackType) || dateToSpriteCycle(feedbackDate)
  );
};

/**
 * Sandbox-mode check: returns true if the feedback date falls within the
 * selected cycle's feedback_start / feedback_end range. This naturally
 * handles Diet SPRiTEs (a date-range subset of the annual cycle) — feedback
 * in the Diet window appears in both the Diet and annual views.
 */
export const isFeedbackInCycleDateRange = (
  feedbackDate: string,
  selectedCycleName: string,
  cycles: ReviewCycle[]
): boolean => {
  const selected = cycles.find((c) => c.cycle_name === selectedCycleName);
  if (!selected) return false;
  const date = new Date(feedbackDate);
  return (
    date >= new Date(selected.feedback_start) &&
    date <= new Date(selected.feedback_end)
  );
};

/** Extract the year from a cycle name (e.g. "2024 SPRiTEs Feedback" -> "2024"). */
export const extractCycleYear = (cycleName: string | null): string | null => {
  if (!cycleName) return null;
  const match = cycleName.match(/\d{4}/);
  return match ? match[0] : null;
};

/** True when the sidebar-selected cycle is a Diet SPRiTEs cycle. */
export const isDietSpritesSelectedCycle = (cycleName: string | null): boolean =>
  cycleName?.includes('Diet SPRiTEs') ?? false;

/** True when a stored review row is for a Diet SPRiTEs review cycle. */
export const isDietSpritesReviewCycle = (reviewCycle: string): boolean =>
  reviewCycle.includes('Diet SPRiTEs');

/**
 * Find the review row for the selected sidebar cycle (self-review source).
 */
export const findReviewForSelectedCycle = (
  reviews: Review[],
  selectedCycle: string | null,
  isSandbox: boolean
): Review | undefined => {
  if (!selectedCycle) return undefined;

  if (isSandbox) {
    return reviews.find((r) => r.review_cycle_name === selectedCycle);
  }

  const selectedYear = extractCycleYear(selectedCycle);
  const isDiet = isDietSpritesSelectedCycle(selectedCycle);

  return reviews.find((r) => {
    const reviewYear = extractCycleYear(r.review_cycle);
    if (reviewYear !== selectedYear) return false;
    const reviewIsDiet = isDietSpritesReviewCycle(r.review_cycle);
    return isDiet ? reviewIsDiet : !reviewIsDiet;
  });
};

const hasDietManagerReviewContent = (review: Review): boolean =>
  Boolean(
    (review.manager_question_2 && review.manager_question_2.trim() !== '') ||
      (review.manager_question_3 && review.manager_question_3.trim() !== '')
  );

/**
 * Find the Diet SPRiTEs manager review for PDF / manager-review display when a Diet cycle is selected.
 */
export const findDietManagerReviewForSelectedCycle = (
  reviews: Review[],
  selectedCycle: string | null,
  isSandbox: boolean
): Review | undefined => {
  if (!selectedCycle || !isDietSpritesSelectedCycle(selectedCycle)) {
    return undefined;
  }

  const withContent = reviews.filter(
    (r) =>
      isDietSpritesReviewCycle(r.review_cycle) && hasDietManagerReviewContent(r)
  );

  if (isSandbox) {
    return withContent.find((r) => r.review_cycle_name === selectedCycle);
  }

  const selectedYear = extractCycleYear(selectedCycle);
  return withContent
    .filter((r) => extractCycleYear(r.review_cycle) === selectedYear)
    .sort((a, b) => {
      const yearA = parseInt(extractCycleYear(a.review_cycle) ?? '0', 10);
      const yearB = parseInt(extractCycleYear(b.review_cycle) ?? '0', 10);
      return yearB - yearA;
    })[0];
};

const hasAnyManagerQuestion = (review: Review): boolean =>
  review.manager_question_1 != null ||
  review.manager_question_2 != null ||
  review.manager_question_3 != null ||
  review.manager_question_4 != null;

/**
 * Find the annual manager review for the selected cycle.
 * Sandbox: match review_cycle_name; prod: same-year annual review.
 */
export const findAnnualManagerReviewForSelectedCycle = (
  reviews: Review[],
  selectedCycle: string | null,
  isSandbox: boolean
): Review | undefined => {
  if (!selectedCycle || isDietSpritesSelectedCycle(selectedCycle)) {
    return undefined;
  }

  if (isSandbox) {
    return reviews.find(
      (r) => r.review_cycle_name === selectedCycle && hasAnyManagerQuestion(r)
    );
  }

  const selectedYear = extractCycleYear(selectedCycle);

  return reviews.find((r) => {
    const reviewYear = extractCycleYear(r.review_cycle);
    return (
      reviewYear === selectedYear &&
      hasAnyManagerQuestion(r) &&
      !isDietSpritesReviewCycle(r.review_cycle)
    );
  });
};
